import React from 'react';
import {
  LineChart, Line, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import { useReceivableTerm } from 'hooks/useReceivableTerm';

const CustomTooltip = ({ active, payload, label, term }) => {
  if (!active || !payload || !payload.length) return null;

  const data = payload[0].payload;
  const pluralLower = term?.pluralLower ?? 'títulos';

  return (
    <div style={{
      background: 'var(--surface)',
      border: '1px solid var(--border)',
      borderRadius: 8,
      padding: 12,
      fontSize: 12,
      boxShadow: 'var(--shadow)'
    }}>
      <div style={{ fontWeight: 'bold', marginBottom: 4, color: 'var(--text-primary)' }}>
        {formatMes(label)}
      </div>
      <div style={{ color: 'var(--status-good)', fontWeight: 'bold' }}>
        {parseFloat(payload[0].value).toFixed(1)}%
      </div>
      <div style={{ color: 'var(--text-muted)', fontSize: 10, marginTop: 2 }}>
        {data.titulosRecebidos?.toLocaleString('pt-BR') || 0} de {data.titulosEmitidos?.toLocaleString('pt-BR') || 0} {pluralLower}
      </div>
    </div>
  );
};

// Constante de meses
const MESES = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

// Função para formatar valores monetários
const formatarValor = (valor) => {
  if (valor >= 1000000) {
    return `R$ ${(valor / 1000000).toFixed(1)}M`;
  } else if (valor >= 1000) {
    return `R$ ${(valor / 1000).toFixed(0)}k`;
  } else {
    return `R$ ${valor.toFixed(0)}`;
  }
};

// Função para formatar mês
const formatMes = (value) => {
  if (!value) return '';
  
  // Se já é um mês formatado (ex: "Jan", "Fev"), retornar como está
  if (MESES.includes(value)) {
    return value;
  }
  
  // Se tem formato YYYY-MM
  if (value.includes('-')) {
    const [ano, mes] = value.split('-');
    const mesIndex = parseInt(mes, 10) - 1;
    
    if (mesIndex >= 0 && mesIndex < 12) {
      return `${MESES[mesIndex]}/${ano.slice(2)}`;
    } else {
      return value;
    }
  }
  
  return value;
};

// Dot customizado
const CustomDot = (props) => {
  const { cx, cy, payload, index } = props;
  const percentual = payload.percentual;
  
  // Definir cor baseada no percentual
  let cor = '#10b981'; // Verde padrão
  if (percentual < 50) cor = '#ef4444'; // Vermelho
  else if (percentual < 70) cor = '#f59e0b'; // Amarelo
  else if (percentual < 85) cor = '#3b82f6'; // Azul
  
  return (
    <circle
      key={`dot-${index}`}
      cx={cx}
      cy={cy}
      r={6}
      fill={cor}
      stroke="#fff"
      strokeWidth={3}
    />
  );
};

const CustomActiveDot = (props) => {
  const { cx, cy, payload, index } = props;
  const percentual = payload.percentual;
  
  // Definir cor baseada no percentual
  let cor = '#10b981'; // Verde padrão
  if (percentual < 50) cor = '#f80505ff'; // Vermelho
  else if (percentual < 70) cor = '#f59e0b'; // Amarelo
  else if (percentual < 85) cor = '#3b82f6'; // Azul
  
  return (
    <circle
      key={`active-dot-${index}`}
      cx={cx}
      cy={cy}
      r={8}
      fill={cor}
      stroke="#fff"
      strokeWidth={3}
    />
  );
};

const GraficoEvolucaoPercentual = ({ dadosGrafico, dadosBrutos }) => {
  const term = useReceivableTerm();

  // Processar dados para calcular percentuais dos últimos 5 meses
  const dadosProcessados = React.useMemo(() => {
    // Usar dados brutos se disponíveis, senão usar dadosGrafico
    const dadosParaProcessar = dadosBrutos || dadosGrafico;
    
    
    if (!dadosParaProcessar || !Array.isArray(dadosParaProcessar) || dadosParaProcessar.length === 0) {
      return [];
    }

    // Pegar os últimos 12 meses (excluindo o mês atual que tem dados zerados)
    const dadosUltimos12Meses = dadosParaProcessar.slice(-13, -1); // Pega os 12 meses anteriores ao atual
    
    return dadosUltimos12Meses.map((item, index) => {
      // Tentar diferentes campos possíveis para os dados
      const titulosEmitidos = item.quantidade_titulos_emitidos || 
                             item.total_titulos_emitidos || 
                             item.titulos_emitidos || 
                             0;
      const titulosRecebidos = item.quantidade_titulos_recebidos || 
                              item.total_titulos_recebidos || 
                              item.titulos_recebidos || 
                              0;
      
      // Extrair valores monetários
      const valorEmitido = parseFloat(item.valor_total_emitido || item.valor_emitido || 0);
      const valorRecebido = parseFloat(item.valor_total_recebido || item.valor_recebido || 0);
      
      // Calcular percentual de recebimento
      const percentual = titulosEmitidos > 0 ? (titulosRecebidos / titulosEmitidos) * 100 : 0;
      
      // Formatar mês
      let mesFormatado = item.mes || `Mês ${index + 1}`;
      if (item.mes && item.mes.includes('-')) {
        const [ano, mes] = item.mes.split('-');
        const mesIndex = parseInt(mes, 10) - 1;
        if (mesIndex >= 0 && mesIndex < 12) {
          mesFormatado = `${MESES[mesIndex]}/${ano.slice(2)}`;
        }
      }

      return {
        mes: mesFormatado,
        percentual: parseFloat(percentual.toFixed(1)),
        titulosEmitidos,
        titulosRecebidos,
        valorEmitido,
        valorRecebido
      };
    });
  }, [dadosGrafico, dadosBrutos]);

  if (!dadosProcessados || dadosProcessados.length === 0) {
    return (
      <div className="p-6">
        <div className="text-center text-[color:var(--text-muted)]">
          <div className="w-16 h-16 mx-auto mb-4">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium mb-2">Evolução de Recebimento</h3>
          <p>Carregando dados de evolução da porcentagem de recebimento...</p>
        </div>
      </div>
    );
  }

  // Calcular estatísticas
  const percentuais = dadosProcessados.map(d => d.percentual);
  const media = percentuais.reduce((acc, val) => acc + val, 0) / percentuais.length;
  const maior = Math.max(...percentuais);
  const percentuaisNaoZero = percentuais.filter(p => p > 0);
  const menor = percentuaisNaoZero.length > 0 ? Math.min(...percentuaisNaoZero) : 0;

  // Calcular estatísticas dos valores monetários
  const valoresEmitidos = dadosProcessados.map(d => d.valorEmitido);
  const valoresRecebidos = dadosProcessados.map(d => d.valorRecebido);

  const mediaValorEmitido = valoresEmitidos.reduce((acc, val) => acc + val, 0) / valoresEmitidos.length;
  const maiorValorEmitido = Math.max(...valoresEmitidos);
  const emitidosNaoZero = valoresEmitidos.filter(v => v > 0);
  const menorValorEmitido = emitidosNaoZero.length > 0 ? Math.min(...emitidosNaoZero) : 0;

  const mediaValorRecebido = valoresRecebidos.reduce((acc, val) => acc + val, 0) / valoresRecebidos.length;
  const maiorValorRecebido = Math.max(...valoresRecebidos);
  const recebidosNaoZero = valoresRecebidos.filter(v => v > 0);
  const menorValorRecebido = recebidosNaoZero.length > 0 ? Math.min(...recebidosNaoZero) : 0;

  return (
    <div className="p-4 h-full rounded-lg border border-[color:var(--border)] bg-[color:var(--surface)] shadow-[0_4px_12px_rgba(0,0,0,0.14)] backdrop-blur">
      <div className="mb-4">
        <h2 className="text-lg font-bold text-[color:var(--text-primary)] mb-1">Evolução da Taxa de Recebimento</h2>
        <p className="text-sm text-[color:var(--chart-card-subtitle)]">Últimos 12 meses - {term.percentualRecebidosSubtitle}</p>
        
        {/* Estatísticas organizadas em cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
          {/* Card Taxa de Recebimento */}
          <div className="rounded-lg p-3 border border-[color:var(--chart-card-blue-border)] bg-[color:var(--chart-card-blue-bg)]">
            <h4 className="text-xs font-semibold text-[color:var(--chart-card-blue-text)] mb-2">Taxa de Recebimento (%)</h4>
            <div className="space-y-1 text-xs">
              <div className="flex justify-between">
                <span className="text-[color:var(--chart-card-label)]">Média:</span>
                <span className="font-semibold text-[color:var(--chart-card-blue-text)]">{media.toFixed(1)}%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[color:var(--chart-card-label)]">Maior:</span>
                <span className="font-semibold text-[color:var(--chart-positive)]">{maior.toFixed(1)}%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[color:var(--chart-card-label)]">Menor:</span>
                <span className="font-semibold text-[color:var(--chart-negative)]">{menor.toFixed(1)}%</span>
              </div>
            </div>
          </div>

          {/* Card Valores Emitidos */}
          <div className="rounded-lg p-3 border border-[color:var(--chart-card-orange-border)] bg-[color:var(--chart-card-orange-bg)]">
            <h4 className="text-xs font-semibold text-[color:var(--chart-card-orange-text)] mb-2">Valores Emitidos</h4>
            <div className="space-y-1 text-xs">
              <div className="flex justify-between">
                <span className="text-[color:var(--chart-card-label)]">Média:</span>
                <span className="font-semibold text-[color:var(--chart-card-orange-text)]">{formatarValor(mediaValorEmitido)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[color:var(--chart-card-label)]">Maior:</span>
                <span className="font-semibold text-[color:var(--chart-positive)]">{formatarValor(maiorValorEmitido)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[color:var(--chart-card-label)]">Menor:</span>
                <span className="font-semibold text-[color:var(--chart-negative)]">{formatarValor(menorValorEmitido)}</span>
              </div>
            </div>
          </div>

          {/* Card Valores Recebidos */}
          <div className="rounded-lg p-3 border border-[color:var(--chart-card-emerald-border)] bg-[color:var(--chart-card-emerald-bg)]">
            <h4 className="text-xs font-semibold text-[color:var(--chart-card-emerald-text)] mb-2">Valores Recebidos</h4>
            <div className="space-y-1 text-xs">
              <div className="flex justify-between">
                <span className="text-[color:var(--chart-card-label)]">Média:</span>
                <span className="font-semibold text-[color:var(--chart-card-emerald-text)]">{formatarValor(mediaValorRecebido)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[color:var(--chart-card-label)]">Maior:</span>
                <span className="font-semibold text-[color:var(--chart-positive)]">{formatarValor(maiorValorRecebido)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[color:var(--chart-card-label)]">Menor:</span>
                <span className="font-semibold text-[color:var(--chart-negative)]">{formatarValor(menorValorRecebido)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={dadosProcessados}
            margin={{ top: 20, right: 30, left: 20, bottom: 35 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis 
              dataKey="mes" 
              stroke="#6b7280"
              fontSize={11}
              tickFormatter={formatMes}
            />
            <YAxis 
              stroke="#6b7280"
              fontSize={10}
              domain={[0, 100]}
              tickFormatter={(value) => `${value}%`}
              label={{
                value: 'Taxa de Recebimento (%)',
                angle: -90,
                position: 'insideLeft',
                style: {
                  textAnchor: 'middle',
                  fontSize: 10,
                  fontWeight: 'bold',
                  fill: '#4b5563'
                }
              }}
            />
            <Tooltip content={(props) => <CustomTooltip {...props} term={term} />} />
            
            <Line
              type="monotone"
              dataKey="percentual"
              stroke="#3b82f6"
              strokeWidth={3}
              dot={CustomDot}
              activeDot={CustomActiveDot}
              name="Taxa de Recebimento"
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      
      {/* Legenda de cores */}
      <div className="mt-3 flex justify-center space-x-4 text-xs">
        <div className="flex items-center">
          <div className="w-3 h-3 bg-red-500 rounded-full mr-1"></div>
          <span className="text-[color:var(--text-muted)]">&lt; 50%</span>
        </div>
        <div className="flex items-center">
          <div className="w-3 h-3 bg-yellow-500 rounded-full mr-1"></div>
          <span className="text-[color:var(--text-muted)]">50-70%</span>
        </div>
        <div className="flex items-center">
          <div className="w-3 h-3 bg-blue-500/100 rounded-full mr-1"></div>
          <span className="text-[color:var(--text-muted)]">70-85%</span>
        </div>
        <div className="flex items-center">
          <div className="w-3 h-3 bg-emerald-500/100 rounded-full mr-1"></div>
          <span className="text-[color:var(--text-muted)]">&gt; 85%</span>
        </div>
      </div>
    </div>
  );
};

export default GraficoEvolucaoPercentual;


